/**
 * ON-334: `'full'` means "target met", not "closed" — role headcounts are a
 * minimum target with unlimited overflow, so there is no full/closed state
 * to represent. It's organizer-only (informational, still open to sign-ups);
 * the participant-facing CaptainView never computes it.
 */
type MarkerState = 'open' | 'partial' | 'full' | 'past'

interface Props {
  state: MarkerState
  /** Render a subtle "yours" highlight (used in captain view). */
  mine?: boolean
}

const COLORS = {
  open: { stroke: 'rgba(198,196,185,0.5)', fill: '#0C0C0C', center: 'transparent' },
  partial: { stroke: '#C2E530', fill: '#0C0C0C', center: '#C2E530' },
  full: { stroke: '#C2E530', fill: '#C2E530', center: 'transparent' },
  past: { stroke: 'rgba(198,196,185,0.3)', fill: '#0C0C0C', center: 'transparent' },
}

/**
 * Transit-style station node — a clean circle that sits ON the rail line.
 * The rail line is drawn by the parent (EventStation) and butts up against
 * the marker, never crossing through it.
 */
export default function StationMarker({ state, mine }: Props) {
  const c = COLORS[state]
  return (
    <div
      className={state === 'partial' || state === 'full' ? 'cp-marker-pulse' : undefined}
      style={{
        position: 'relative',
        width: 22,
        height: 22,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
      }}
    >
      <svg width="22" height="22" viewBox="0 0 22 22" aria-hidden="true">
        <circle
          cx="11"
          cy="11"
          r="8"
          fill={c.fill}
          stroke={c.stroke}
          strokeWidth="1.5"
        />
        {c.center !== 'transparent' && (
          <circle cx="11" cy="11" r="2.5" fill={c.center} />
        )}
      </svg>
      {mine && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: -3,
            borderRadius: '50%',
            border: '1px solid rgba(194,229,48,0.55)',
          }}
        />
      )}
    </div>
  )
}

export type { MarkerState }
